// frontend/src/context/AuthContext.jsx
import { createContext, useState, useContext, useEffect } from "react";
import api from "../config/api";

const AuthContext = createContext();

const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 horas

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar autenticación con el backend
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(null);
        return false;
      }

      const response = await api.get('/user');
      if (response.data) {
        setUser(response.data);
        return true;
      }
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('login_time');
      setUser(null);
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");
      const loginTime = localStorage.getItem("login_time");
      
      if (storedUser && token && loginTime) {
        if (isSessionExpired(parseInt(loginTime))) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("login_time");
          setUser(null);
        } else {
          const isValid = await checkAuth();
          if (!isValid) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("login_time");
            setUser(null);
          }
        }
      }
      
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const isSessionExpired = (loginTimestamp) => {
    if (!loginTimestamp) return true;
    const now = Date.now();
    const elapsed = now - loginTimestamp;
    return elapsed > SESSION_DURATION;
  };

  // Login
  const login = async (email, password) => {
    try {
      // Validar datos
      if (!email || !password) {
        return { 
          success: false, 
          message: 'Email e senha são obrigatórios' 
        };
      }

      // Obtener CSRF token
      await api.get('/sanctum/csrf-cookie');
      
      // Hacer login
      const response = await api.post('/auth/login', { email, password });
      
      // Verificar si el login fue exitoso
      if (response.data.success === true) {
        const userData = response.data.user;
        const token = response.data.token;
        const loginTime = Date.now();
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('login_time', loginTime.toString());
        
        setUser(userData);
        
        return { success: true };
      }
      
      // Fallback
      if (response.data.user && response.data.token) {
        const userData = response.data.user;
        const token = response.data.token;
        const loginTime = Date.now();
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('login_time', loginTime.toString());
        
        setUser(userData);
        
        return { success: true };
      }
      
      return { 
        success: false, 
        message: response.data.message || 'Erro ao fazer login' 
      };
      
    } catch (error) {
      let errorMessage = 'Erro ao fazer login';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Email ou senha incorretos';
      } else if (error.response?.status === 403) {
        errorMessage = 'Conta em validação. Aguarde aprovação.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Dados inválidos. Verifique os campos.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Sem conexão com o servidor. Verifique sua rede.';
      }
      
      return { 
        success: false, 
        message: errorMessage 
      };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      // Silenciar errores de logout
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('login_time');
      setUser(null);
      
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    
    window.dispatchEvent(new CustomEvent("user-updated", { 
      detail: updatedUserData 
    }));
  };

  const getSessionTimeRemaining = () => {
    const loginTime = localStorage.getItem("login_time");
    if (!loginTime) return 0;
    const elapsed = Date.now() - parseInt(loginTime);
    const remaining = SESSION_DURATION - elapsed;
    return remaining > 0 ? remaining : 0;
  };

  const getFormattedSessionTime = () => {
    const remaining = getSessionTimeRemaining();
    if (remaining <= 0) return "Sessão expirada";
    
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes} minutos`;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout,
      updateUser,
      getSessionTimeRemaining,
      getFormattedSessionTime
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);